import React, { useContext, useEffect, useMemo } from "react";
import "./leaderboardScreen.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { ChevronLeft, ChevronRight, People } from "./components/icons";
import { useObservableProperty } from "../observable/useObservableProperty";
import { SimpleDate, areSimpleDatesEqual, compareSimpleDates } from "../dataLayer/SimpleDate";
import { toPuzzleId } from "../dataLayer/backendApi";
import type { LeaderboardRow } from "../dataLayer/DataLayer";
import { PremiumAccessCard } from "./components/premiumAccessCard";
import { TierStar } from "./components/tierStar";

const DASH = "\u2014";

/** Big weekday/"Today" label for the date header. */
function dateHeadline(d: SimpleDate, isToday: boolean): string {
  if (isToday) return "Today";
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    weekday: "long",
  });
}

/** Secondary "July 5, 2026" line under the headline. */
function dateSubline(d: SimpleDate): string {
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** A placeholder dash for values that aren't applicable yet. Rendered in the
 *  UI font and muted so every column's dash looks identical (the Total cell
 *  otherwise uses the display serif, whose em-dash reads shorter). */
const Dash = () => <span className="lbtable__dash">{DASH}</span>;

/** One numeric cell: the value, or a shared placeholder dash. */
function cell(value: number | undefined): React.ReactNode {
  return value === undefined ? <Dash /> : value.toLocaleString();
}

export function LeaderboardScreen() {
  const dataLayer = useContext(DataLayerContext);
  const leaderboard = useObservableProperty(dataLayer.leaderboard);
  const premium = useObservableProperty(dataLayer.premium);
  const roster = useObservableProperty(dataLayer.roster);
  // The selected day lives on the DataLayer (not local state) so it survives
  // this screen unmounting when you open a friend's play and press Back.
  const selectedDate = useObservableProperty(dataLayer.leaderboardDate);

  // The player's known puzzle dates, newest first. Index 0 is today (or the
  // most recent day); larger indices step further back in time.
  const dates = useMemo(() => {
    const list = dataLayer.puzzles.slice();
    list.sort((a, b) => compareSimpleDates(b.date, a.date));
    return list.map((p) => ({ date: p.date, isToday: p.isToday }));
  }, [dataLayer.puzzles]);

  // Resolve the selected day to an index into `dates`. Undefined (a fresh open)
  // or a day no longer present falls back to the most recent day.
  const safeIndex = useMemo(() => {
    if (selectedDate) {
      const i = dates.findIndex((d) => areSimpleDatesEqual(d.date, selectedDate));
      if (i >= 0) return i;
    }
    return 0;
  }, [dates, selectedDate]);
  const current = dates[safeIndex];

  // Load the selected day's leaderboard whenever the day (or roster) changes.
  useEffect(() => {
    if (current) void dataLayer.loadLeaderboard(current.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeIndex, roster]);

  const onBack = () => dataLayer.navigateBack();
  const onManageFriends = () => dataLayer.navigateTo("Friends");

  // "<" steps back in time (older); ">" steps toward today (newer).
  const hasOlder = safeIndex < dates.length - 1;
  const hasNewer = safeIndex > 0;
  const goOlder = () => {
    if (hasOlder) dataLayer.leaderboardDate.value = dates[safeIndex + 1].date;
  };
  const goNewer = () => {
    if (hasNewer) dataLayer.leaderboardDate.value = dates[safeIndex - 1].date;
  };

  const puzzleId = current ? toPuzzleId(current.date) : "";
  const generationMatches = leaderboard !== null && leaderboard.generation === premium.generation;
  const showingRows =
    generationMatches && current && leaderboard.puzzleId === puzzleId ? leaderboard.rows : [];
  const loading =
    !leaderboard || !generationMatches || leaderboard.loading || leaderboard.puzzleId !== puzzleId;
  const scoresVisible =
    generationMatches &&
    premium.status === "ready" &&
    premium.tier !== null &&
    leaderboard.scoresVisible;
  const hasFriends = (roster?.friends.length ?? 0) > 0;

  // On past days everyone's words are already public (the review screen shows
  // them), so any played row is viewable. On today's puzzle we only reveal a
  // player's words once *you* have finished today — you're then locked in for
  // the day, so there's nothing left to spoil.
  const selfPlayedToday = showingRows.some((r: LeaderboardRow) => r.isSelf && r.entry.played);
  const canViewWords = (row: LeaderboardRow): boolean =>
    row.entry.played && current !== undefined && (!current.isToday || selfPlayedToday);

  return (
    <div className="screen leaderboard">
      <AppBar onBack={onBack} />
      <PageHeading eyebrow="Friends" title="Leaderboard" />

      <div className="lb__body">
        {!scoresVisible && <PremiumAccessCard context="leaderboard" />}

        {scoresVisible && (
          <div className="lb__datenav">
            <button
              className="lb__arrow"
              onClick={goNewer}
              disabled={!hasNewer}
              aria-label="Newer day"
            >
              <ChevronLeft />
            </button>
            <div className="lb__date">
              <div className="lb__date-main display">
                {current ? dateHeadline(current.date, current.isToday) : DASH}
              </div>
              {current && <div className="lb__date-sub">{dateSubline(current.date)}</div>}
            </div>
            <button
              className="lb__arrow"
              onClick={goOlder}
              disabled={!hasOlder}
              aria-label="Older day"
            >
              <ChevronRight />
            </button>
          </div>
        )}

        {!hasFriends && (
          <p className="lb__empty">
            It&rsquo;s just you so far. Add friends to see how you stack up.
          </p>
        )}

        {loading ? (
          <div className="lb__status">{"Loading scores\u2026"}</div>
        ) : (
          <div className="lb__tablewrap">
            <table className={`lbtable${scoresVisible ? "" : " lbtable--names-only"}`}>
              <thead>
                <tr>
                  {scoresVisible && (
                    <th className="lbtable__rank" scope="col">
                      #
                    </th>
                  )}
                  <th className="lbtable__name" scope="col">
                    Player
                  </th>
                  {scoresVisible && (
                    <>
                      <th className="lbtable__n" scope="col">
                        Words
                      </th>
                      <th className="lbtable__n" scope="col">
                        Reg
                      </th>
                      <th className="lbtable__n" scope="col">
                        Rare
                      </th>
                      <th className="lbtable__n lbtable__total" scope="col">
                        Total
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {showingRows.map((row: LeaderboardRow, i: number) => {
                  const e = row.entry;
                  const canView = canViewWords(row);
                  const rank = e.played ? i + 1 : undefined;
                  return (
                    <tr
                      key={row.playerId}
                      className={`lbtable__row${row.isSelf ? " lbtable__row--self" : ""}${
                        e.played ? "" : " lbtable__row--out"
                      }`}
                    >
                      {scoresVisible && (
                        <td className="lbtable__rank num">
                          <span
                            className={`lbtable__rank-value${
                              row.tier ? " lbtable__rank-value--supporter" : ""
                            }${
                              rank !== undefined && rank >= 10 && rank < 100
                                ? " lbtable__rank-value--medium"
                                : ""
                            }${
                              rank !== undefined && rank >= 100 ? " lbtable__rank-value--wide" : ""
                            }`}
                          >
                            {rank ?? <Dash />}
                          </span>
                          {row.tier && (
                            <TierStar
                              className="lbtable__premium lbtable__premium--rank"
                              variant="rank"
                              tier={row.tier}
                            />
                          )}
                        </td>
                      )}
                      <td
                        className={`lbtable__name${
                          !scoresVisible && row.tier ? " lbtable__name--premium" : ""
                        }`}
                      >
                        {canView ? (
                          <button
                            type="button"
                            className="lbtable__player-link"
                            aria-label={`See the words ${row.displayName} found`}
                            onClick={() => dataLayer.viewFriendPlay(row, current.date)}
                          >
                            <span className="lbtable__player">{row.displayName}</span>
                            <ChevronRight className="lbtable__view" />
                          </button>
                        ) : (
                          <span className="lbtable__player">{row.displayName}</span>
                        )}
                        {!scoresVisible && row.tier && (
                          <TierStar
                            className="lbtable__premium lbtable__premium--name"
                            variant="name"
                            tier={row.tier}
                          />
                        )}
                        {scoresVisible && !e.played && (
                          <span className="lbtable__note">Not played</span>
                        )}
                      </td>
                      {scoresVisible && (
                        <>
                          <td className="lbtable__n num">
                            {e.played ? cell(e.wordCount) : <Dash />}
                          </td>
                          <td className="lbtable__n num">
                            {e.played ? cell(e.regularPoints) : <Dash />}
                          </td>
                          <td className="lbtable__n num">{cell(e.rarityPoints)}</td>
                          <td className="lbtable__n lbtable__total num">{cell(e.totalPoints)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <button className="btn btn--wide lb__manage" onClick={onManageFriends}>
          <People className="btn__icon" />
          Manage friends
        </button>
      </div>
    </div>
  );
}
